import { BaseApiService, API_CONFIG } from './base.service';
import {
  Contract,
  ContractCreate,
  ContractLine,
  ContractLineCreate,
  PaginatedResponse
} from '../../types';
import { ensureValidContractBillingCycle } from '../../utils/billing-mappings';
import { normalizeLanguageCode } from '../../utils/language';
import { ContractBillingCycleValue } from '../../constants/billing';

/**
 * Service for managing contracts and contract lines.
 * Provides methods for creating, retrieving, updating, and deleting contracts and their lines.
 */
export class ContractService extends BaseApiService {
  /**
   * Retrieves a paginated list of contracts.
   * @param page - The page number to retrieve.
   * @param pageSize - The number of contracts per page.
   * @param isActive - Filter by active status (true for active, false for inactive, undefined for all).
   * @param customerId - Filter by customer ID.
   * @returns A promise that resolves to a paginated response of contracts.
   */
  async getContracts(
    page: number = 1, 
    pageSize: number = 10, 
    isActive?: boolean, 
    customerId?: number,
    search?: string,
    hideInactiveExpired?: boolean,
    hideWithoutCustomer?: boolean,
    orderBy?: string,
    orderDir: 'asc' | 'desc' = 'asc',
    status?: 'all' | 'active' | 'deleted'
  ): Promise<PaginatedResponse<Contract>> {
    const params = new URLSearchParams();
    
    // Convert page to skip for backend API
    const skip = (page - 1) * pageSize;
    params.append('skip', skip.toString());
    params.append('limit', pageSize.toString());
    
    if (isActive !== undefined) {
      params.append('is_active', isActive.toString());
    }
    
    if (customerId !== undefined) {
      params.append('customer_id', customerId.toString());
    }
    
    if (search) {
      params.append('search', search);
    }
    
    if (hideInactiveExpired !== undefined) {
      params.append('hide_inactive_expired', hideInactiveExpired.toString());
    }
    
    if (hideWithoutCustomer !== undefined) {
      params.append('hide_without_customer', hideWithoutCustomer.toString());
    }

    if (orderBy) {
      params.append('order_by', orderBy);
      params.append('order_dir', orderDir);
    }

    if (status) {
      params.append('status', status);
    }
    
    const response = await this.get<Contract[]>(`${API_CONFIG.ENDPOINTS.CONTRACTS}?${params.toString()}`);
    
    // Since the backend doesn't return pagination metadata for this endpoint,
    // we need to estimate the total pages based on the response
    // If we get a full page, there might be more pages
    const hasNext = response.length === pageSize;
    const totalPages = hasNext ? page + 1 : page;
    
    return {
      items: response,
      total: response.length, // This is just the current page count
      page: page,
      pageSize: pageSize,
      totalPages: totalPages
    };
  }

  /**
   * Retrieves a single contract by its ID.
   * @param id - The ID of the contract to retrieve.
   * @returns A promise that resolves to the contract.
   */
  async getContract(id: string): Promise<Contract> {
    return this.get(`${API_CONFIG.ENDPOINTS.CONTRACTS}/${id}`);
  }

  /**
   * Creates a new contract.
   * Handles both regular and supervisor contracts.
   * Normalizes the language code to lowercase.
   * @param contract - The contract creation data.
   * @returns A promise that resolves to the created contract or a supervisor contract response.
   */
  async createContract(contract: ContractCreate): Promise<Contract> {
    // Normalize language using utility to ensure it matches backend enum
    const payload = {
      ...contract,
      language: normalizeLanguageCode(contract.language),
    } as const;
    
    const response = await this.post<Contract>(`${API_CONFIG.ENDPOINTS.CONTRACTS}/`, payload);
    
    return response;
  }

  /**
   * Updates an existing contract.
   * @param id - The ID of the contract to update.
   * @param contract - The partial contract data to update.
   * @returns A promise that resolves to the updated contract.
   */
  async updateContract(id: string, contract: Partial<ContractCreate>): Promise<Contract> {
    const payload = { ...contract };
    if (payload.language) {
      payload.language = normalizeLanguageCode(payload.language);
    }
    return this.put(`${API_CONFIG.ENDPOINTS.CONTRACTS}/${id}`, payload);
  }

  /**
   * Deletes a contract by its ID.
   * @param id - The ID of the contract to delete.
   */
  async deleteContract(id: string): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.CONTRACTS}/${id}`);
  }

  /**
   * Bulk deletes multiple contracts.
   * @param contractIds - Array of contract IDs to delete.
   * @param softDelete - Whether to perform soft delete (default: true).
   * @returns A promise that resolves to the bulk delete result.
   */
  async bulkDeleteContracts(contractIds: string[], softDelete: boolean = true): Promise<any> {
    return this.post(`${API_CONFIG.ENDPOINTS.CONTRACTS}/bulk-delete?soft_delete=${softDelete}`, contractIds);
  }

  /**
   * Retrieves all contract lines for a given contract.
   * @param contractId - The ID of the contract.
   * @returns A promise that resolves to an array of contract lines.
   */
  async getContractLines(contractId: string): Promise<ContractLine[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.CONTRACTS}/${contractId}/lines`);
  }

  /**
   * Creates a new contract line.
   * @param contractLine - The contract line creation data.
   * @returns A promise that resolves to the created contract line.
   */
  async createContractLine(contractLine: ContractLineCreate): Promise<ContractLine> {
    const sanitizedData: ContractLineCreate = {
      ...contractLine,
      billing_cycle: contractLine.billing_cycle
        ? ensureValidContractBillingCycle(contractLine.billing_cycle) as ContractBillingCycleValue
        : undefined,
    };
    return this.post(`${API_CONFIG.ENDPOINTS.CONTRACT_LINES}/`, sanitizedData);
  }

  /**
   * Updates an existing contract line.
   * @param id - The ID of the contract line to update.
   * @param contractLine - The partial contract line data to update.
   * @returns A promise that resolves to the updated contract line.
   */
  async updateContractLine(id: string, contractLine: Partial<ContractLineCreate>): Promise<ContractLine> {
    const sanitizedData: Partial<ContractLineCreate> = contractLine.billing_cycle
      ? {
          ...contractLine,
          billing_cycle: ensureValidContractBillingCycle(contractLine.billing_cycle) as ContractBillingCycleValue,
        }
      : contractLine;
    return this.put(`${API_CONFIG.ENDPOINTS.CONTRACT_LINES}/${id}`, sanitizedData);
  }

  /**
   * Deletes a contract line by its ID.
   * @param id - The ID of the contract line to delete.
   */
  async deleteContractLine(id: string): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.CONTRACT_LINES}/${id}`);
  }

  /**
   * Bulk updates dates for multiple contract lines.
   * @param contractLineIds - Array of contract line IDs to update.
   * @param startDate - New start date (optional).
   * @param endDate - New end date (optional).
   * @param trialPeriod - New trial period in days (optional).
   * @param updatedBy - User performing the update.
   * @returns A promise that resolves to the bulk update result.
   */
  async bulkUpdateContractLineDates(
    contractLineIds: string[],
    startDate?: string,
    endDate?: string,
    trialPeriod?: number,
    updatedBy: string = 'system'
  ): Promise<{
    success_count: number;
    error_count: number;
    total_count: number;
    errors: Array<{ contractline_id: string; error: string }>;
    updated_contract_lines: string[];
  }> {
    const payload = {
      contractline_ids: contractLineIds,
      start_date: startDate,
      end_date: endDate,
      trial_period: trialPeriod,
      updated_by: updatedBy
    };

    return this.post(`${API_CONFIG.ENDPOINTS.CONTRACT_LINES}/bulk-update-dates`, payload);
  }

  /**
   * Recalculate last/upcoming billing periods for all contracts or a single contract
   * Pass a contractId to limit to one contract; omit to recalculate all
   */
  async syncBillingPeriods(contractId?: string): Promise<any> {
    const payload = contractId ? contractId : null;
    return this.post(`${API_CONFIG.ENDPOINTS.CONTRACTS}/sync-billing-periods`, payload);
  }

  /**
   * Get detailed debugging information about billing period calculations
   * @param contractId The contract ID to debug
   * @returns Promise with debug information
   */
  async getBillingPeriodsDebug(contractId: string): Promise<any> {
    try {
      return await this.get(`${API_CONFIG.ENDPOINTS.CONTRACTS}/${contractId}/billing-periods-debug`);
    } catch (error) {
      console.error('Error getting billing periods debug info:', error);
      throw error;
    }
  }
}

export const contractService = new ContractService();